import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import {
  Upload,
  FileImage,
  Crop,
  Sparkles,
  Scissors,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Download,
  RotateCcw,
  MousePointer,
  Square
} from 'lucide-react';
import { toast } from 'sonner';
import HUDPageHeader from '../components/HUDPageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanPrepPage = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1=upload, 2=edit, 3=segment
  const [prepDoc, setPrepDoc] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [cleaning, setCleaning] = useState(false);
  const [cropping, setCropping] = useState(false);
  const [cropMode, setCropMode] = useState(false);
  const [cropRect, setCropRect] = useState(null);
  // Segment state
  const [segmentMode, setSegmentMode] = useState(false);
  const [segments, setSegments] = useState([]);
  const [drawingSegment, setDrawingSegment] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [creating, setCreating] = useState(false);
  const canvasRef = useRef(null);
  const segmentCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [dragFile, setDragFile] = useState(false);
  // Refs for drawing state (avoids stale closure issues)
  const drawStateRef = useRef({ start: null, current: null, cropMode: false, segmentMode: false });

  useEffect(() => {
    axios.get(`${API}/projects`, { withCredentials: true })
      .then(r => setProjects(r.data))
      .catch(() => {});
  }, []);

  // Keep drawStateRef in sync with mode states
  useEffect(() => {
    drawStateRef.current.cropMode = cropMode;
    drawStateRef.current.segmentMode = segmentMode;
  }, [cropMode, segmentMode]);

  // Native event listeners for segment canvas (avoids React stale closure issues)
  useEffect(() => {
    const el = segmentCanvasRef.current;
    if (!el || !segmentMode) return;

    const getCoords = (e) => {
      const rect = el.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onDown = (e) => {
      const coords = getCoords(e);
      drawStateRef.current.start = coords;
      drawStateRef.current.current = { x: coords.x, y: coords.y, width: 0, height: 0 };
      setDrawingSegment({ x: coords.x, y: coords.y, width: 0, height: 0 });
    };

    const onMove = (e) => {
      const start = drawStateRef.current.start;
      if (!start) return;
      const coords = getCoords(e);
      const rect = {
        x: Math.min(start.x, coords.x),
        y: Math.min(start.y, coords.y),
        width: Math.abs(coords.x - start.x),
        height: Math.abs(coords.y - start.y)
      };
      drawStateRef.current.current = rect;
      setDrawingSegment(rect);
    };

    const onUp = () => {
      const seg = drawStateRef.current.current;
      if (seg && seg.width > 10 && seg.height > 10) {
        setSegments(prev => [...prev, {
          ...seg,
          name: `Lot ${prev.length + 1}`,
          id: Date.now()
        }]);
      }
      drawStateRef.current.start = null;
      drawStateRef.current.current = null;
      setDrawingSegment(null);
    };

    el.addEventListener('mousedown', onDown);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseup', onUp);
    return () => {
      el.removeEventListener('mousedown', onDown);
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseup', onUp);
    };
  }, [segmentMode]);


  const loadImage = useCallback((prepId) => {
    const url = `${API}/plan-prep/${prepId}/image?t=${Date.now()}`;
    setImageUrl(url);
  }, []);

  // Upload handler
  const handleUpload = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type) && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Format non supporté. Utilisez PDF, PNG ou JPG.');
      return;
    }

    setUploading(true);
    setConverting(file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
    setConversionProgress(0);

    // Simulate conversion progress
    const interval = setInterval(() => {
      setConversionProgress(p => Math.min(p + Math.random() * 15, 90));
    }, 300);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/plan-prep/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      clearInterval(interval);
      setConversionProgress(100);
      setPrepDoc(res.data);
      loadImage(res.data.prep_id);
      setTimeout(() => {
        setStep(2);
        setConverting(false);
      }, 500);
    } catch (error) {
      clearInterval(interval);
      toast.error(error.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragFile(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  // Canvas interaction
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleCanvasMouseDown = (e) => {
    const coords = getCanvasCoords(e);
    if (cropMode) {
      drawStateRef.current.start = coords;
      setCropRect({ x: coords.x, y: coords.y, width: 0, height: 0 });
    }
  };

  const handleCanvasMouseMove = (e) => {
    const start = drawStateRef.current.start;
    if (!start || !cropMode) return;
    const coords = getCanvasCoords(e);
    const rect = {
      x: Math.min(start.x, coords.x),
      y: Math.min(start.y, coords.y),
      width: Math.abs(coords.x - start.x),
      height: Math.abs(coords.y - start.y)
    };
    setCropRect(rect);
  };

  const handleCanvasMouseUp = () => {
    drawStateRef.current.start = null;
  };

  // Image loaded handler
  const handleImageLoad = () => {
    const img = imgRef.current;
    const container = canvasRef.current || segmentCanvasRef.current;
    if (img && container) {
      setCanvasSize({ w: container.offsetWidth, h: container.offsetHeight });
    }
  };

  // Crop action
  const handleCrop = async () => {
    if (!cropRect || !prepDoc) return;
    setCropping(true);
    try {
      const res = await axios.post(`${API}/plan-prep/${prepDoc.prep_id}/crop`, {
        x: cropRect.x,
        y: cropRect.y,
        width: cropRect.width,
        height: cropRect.height,
        canvasWidth: canvasSize.w,
        canvasHeight: canvasSize.h
      }, { withCredentials: true });
      toast.success('Image recadrée');
      setPrepDoc(prev => ({ ...prev, width: res.data.width, height: res.data.height }));
      loadImage(prepDoc.prep_id);
      setCropRect(null);
      setCropMode(false);
    } catch (error) {
      toast.error('Erreur recadrage');
    } finally {
      setCropping(false);
    }
  };

  // AI Clean
  const handleAIClean = async () => {
    if (!prepDoc) return;
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/plan-prep/${prepDoc.prep_id}/clean`, {}, {
        withCredentials: true,
        timeout: 120000
      });
      toast.success('Nettoyage IA terminé !');
      setPrepDoc(prev => ({ ...prev, width: res.data.width, height: res.data.height, ai_cleaned: true }));
      loadImage(prepDoc.prep_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur nettoyage IA');
    } finally {
      setCleaning(false);
    }
  };

  // Create sub-projects
  const handleCreateSubProjects = async () => {
    if (!selectedProject || segments.length === 0) {
      toast.error('Sélectionnez un projet et dessinez au moins un lot');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/plan-prep/${prepDoc.prep_id}/segment`, {
        project_id: selectedProject,
        rectangles: segments.map(s => ({ x: s.x, y: s.y, width: s.width, height: s.height, name: s.name })),
        canvasWidth: canvasSize.w,
        canvasHeight: canvasSize.h
      }, { withCredentials: true });
      toast.success(`${res.data.lots.length} lots créés avec succès !`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const removeSegment = (id) => {
    setSegments(prev => prev.filter(s => s.id !== id));
  };

  const renameSegment = (id, name) => {
    setSegments(prev => prev.map(s => s.id === id ? { ...s, name } : s));
  };

  const resetAll = () => {
    setStep(1);
    setPrepDoc(null);
    setImageUrl(null);
    setCropRect(null);
    setCropMode(false);
    setSegments([]);
    setSegmentMode(false);
    drawStateRef.current = { start: null, current: null, cropMode: false, segmentMode: false };
  };

  const COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

  return (
    <div data-hud-page data-testid="plan-prep-page" className="fade-in">
      <HUDPageHeader
        module="Cockpit"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Préparation' }]}
        icon={Crop}
        title="Préparation des Plans"
        subtitle="Convertissez, nettoyez et segmentez vos plans avant génération 3D"
        actions={step > 1 && (
          <button
            onClick={resetAll}
            data-testid="reset-btn"
            className="hud-btn hud-btn-secondary hud-btn-md"
          >
            <RotateCcw className="w-4 h-4" />
            Nouveau plan
          </button>
        )}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6">

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
        {[
          { n: 1, label: 'Upload & Conversion', icon: Upload },
          { n: 2, label: 'Édition & Nettoyage', icon: Crop },
          { n: 3, label: 'Segmentation', icon: Scissors }
        ].map(({ n, label, icon: Icon }) => (
          <div key={n} className="flex items-center gap-2 shrink-0">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              step === n ? 'bg-blue-600 text-white' :
              step > n ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {step > n ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">Étape {n}</span>
            </div>
            {n < 3 && <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />}
          </div>
        ))}
      </div>

      {/* STEP 1: Upload & Conversion */}
      {step === 1 && (
        <div className="max-w-2xl mx-auto">
          <div
            className={`border-2 border-dashed rounded-2xl p-8 sm:p-16 text-center transition-colors cursor-pointer ${
              dragFile ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragFile(true); }}
            onDragLeave={() => setDragFile(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('plan-upload-input').click()}
            data-testid="upload-zone"
          >
            <input
              id="plan-upload-input"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])}
            />
            {uploading || converting ? (
              <div className="space-y-4">
                <Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin" />
                <p className="text-lg font-medium text-gray-900">
                  {converting ? 'Conversion PDF → PNG HD' : 'Upload en cours...'}
                </p>
                <Progress value={conversionProgress} className="h-2 max-w-xs mx-auto" />
                <p className="text-sm text-gray-500">{Math.round(conversionProgress)}%</p>
              </div>
            ) : (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FileImage className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-lg font-medium text-gray-900 mb-2">Glissez votre plan ici</p>
                <p className="text-sm text-gray-500 mb-4">ou cliquez pour sélectionner un fichier</p>
                <p className="text-xs text-gray-400">Formats acceptés : PDF, PNG, JPG (max 50 Mo)</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* STEP 2: Edit & Clean */}
      {step === 2 && prepDoc && (
        <div>
          {/* Toolbar */}
          <div className="flex flex-wrap gap-2 mb-4 p-3 bg-white rounded-xl border border-gray-200">
            <Button
              variant={cropMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setCropMode(!cropMode); setSegmentMode(false); setCropRect(null); }}
              data-testid="crop-tool-btn"
            >
              <Crop className="w-4 h-4 mr-1.5" />
              Recadrer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAIClean}
              disabled={cleaning}
              className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
              data-testid="ai-clean-btn"
            >
              {cleaning ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Nettoyage IA...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-1.5" />Nettoyage IA</>
              )}
            </Button>
            {cropMode && cropRect && cropRect.width > 10 && (
              <Button size="sm" onClick={handleCrop} disabled={cropping} className="bg-blue-600 hover:bg-blue-700" data-testid="apply-crop-btn">
                {cropping ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                Appliquer le recadrage
              </Button>
            )}
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => { setStep(3); setCropMode(false); setCropRect(null); }} data-testid="next-step-btn">
              Segmentation <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>

          {/* Canvas */}
          <div
            ref={canvasRef}
            className="relative bg-gray-100 rounded-xl border border-gray-200 overflow-hidden"
            style={{ minHeight: 400 }}
            onMouseDown={cropMode ? handleCanvasMouseDown : undefined}
            onMouseMove={cropMode ? handleCanvasMouseMove : undefined}
            onMouseUp={cropMode ? handleCanvasMouseUp : undefined}
            data-testid="edit-canvas"
          >
            {imageUrl && (
              <img
                ref={imgRef}
                src={imageUrl}
                alt="Plan"
                crossOrigin="use-credentials"
                className="w-full h-auto block"
                onLoad={handleImageLoad}
              />
            )}
            {/* Crop overlay */}
            {cropMode && cropRect && (
              <>
                <div className="absolute inset-0 bg-black/40 pointer-events-none" />
                <div
                  className="absolute border-2 border-blue-500 bg-transparent pointer-events-none"
                  style={{
                    left: cropRect.x, top: cropRect.y,
                    width: cropRect.width, height: cropRect.height,
                    boxShadow: `0 0 0 9999px rgba(0,0,0,0.4)`
                  }}
                  data-testid="crop-rect"
                />
              </>
            )}
            {/* Mode indicator */}
            {cropMode && (
              <div className="absolute top-3 left-3 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                Mode recadrage — dessinez la zone
              </div>
            )}
          </div>

          {/* Info bar */}
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
            <span>{prepDoc.width} × {prepDoc.height} px</span>
            <span>{(prepDoc.size_bytes / 1024).toFixed(0)} Ko</span>
            {prepDoc.was_pdf && <span className="text-green-600 font-medium">Converti depuis PDF</span>}
            {prepDoc.ai_cleaned && <span className="text-purple-600 font-medium">Nettoyé par IA</span>}
          </div>
        </div>
      )}

      {/* STEP 3: Segmentation */}
      {step === 3 && prepDoc && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Canvas */}
          <div>
            {/* Toolbar */}
            <div className="flex flex-wrap gap-2 mb-4 p-3 bg-white rounded-xl border border-gray-200">
              <Button size="sm" variant="outline" onClick={() => setStep(2)} data-testid="back-to-edit">
                <ArrowLeft className="w-4 h-4 mr-1.5" />Retour
              </Button>
              <Button
                variant={segmentMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSegmentMode(!segmentMode)}
                data-testid="segment-tool-btn"
              >
                <Square className="w-4 h-4 mr-1.5" />
                {segmentMode ? 'Dessiner les lots' : 'Activer la sélection'}
              </Button>
              {!segmentMode && (
                <span className="text-xs text-gray-500 flex items-center"><MousePointer className="w-3 h-3 mr-1" />Cliquez pour activer le mode sélection</span>
              )}
            </div>

            <div
              ref={segmentCanvasRef}
              className="relative bg-gray-100 rounded-xl border border-gray-200 overflow-hidden"
              style={{ minHeight: 400, cursor: segmentMode ? 'crosshair' : 'default' }}
              data-testid="segment-canvas"
            >
              {imageUrl && (
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Plan"
                  crossOrigin="use-credentials"
                  className="w-full h-auto block"
                  onLoad={handleImageLoad}
                />
              )}
              {/* Drawn segments */}
              {segments.map((seg, i) => (
                <div
                  key={seg.id}
                  className="absolute border-2 pointer-events-none"
                  style={{
                    left: seg.x, top: seg.y,
                    width: seg.width, height: seg.height,
                    borderColor: COLORS[i % COLORS.length],
                    backgroundColor: `${COLORS[i % COLORS.length]}15`
                  }}
                >
                  <span
                    className="absolute -top-5 left-0 text-[10px] font-bold text-white px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}
                  >
                    {seg.name}
                  </span>
                </div>
              ))}
              {/* Drawing segment */}
              {drawingSegment && (
                <div
                  className="absolute border-2 border-dashed border-blue-500 bg-blue-500/10 pointer-events-none"
                  style={{
                    left: drawingSegment.x, top: drawingSegment.y,
                    width: drawingSegment.width, height: drawingSegment.height
                  }}
                />
              )}
              {segmentMode && (
                <div className="absolute top-3 left-3 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Dessinez des rectangles pour chaque lot
                </div>
              )}
            </div>
          </div>

          {/* Right panel: Lot list */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3">Lots créés ({segments.length})</h3>
              {segments.length === 0 ? (
                <p className="text-sm text-gray-500">Dessinez des rectangles sur le plan pour créer des lots</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {segments.map((seg, i) => (
                    <div key={seg.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className="w-3 h-3 rounded shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <Input
                        value={seg.name}
                        onChange={(e) => renameSegment(seg.id, e.target.value)}
                        className="h-8 text-sm flex-1"
                        data-testid={`lot-name-${i}`}
                      />
                      <Button
                        variant="ghost" size="icon"
                        className="h-7 w-7 text-red-500 hover:bg-red-50 shrink-0"
                        onClick={() => removeSegment(seg.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Project selector + Create button */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3">Rattacher au projet</h3>
              <Select value={selectedProject || 'none'} onValueChange={(v) => v !== 'none' && setSelectedProject(v)}>
                <SelectTrigger data-testid="segment-project-select"><SelectValue placeholder="Choisir un projet" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Choisir un projet</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                onClick={handleCreateSubProjects}
                disabled={creating || segments.length === 0 || !selectedProject}
                data-testid="create-sub-projects-btn"
              >
                {creating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" />Créer {segments.length} sous-projet{segments.length > 1 ? 's' : ''}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PlanPrepPage;
